import { OptionType } from '@medely/web-components';
import useCurrentUser from 'hooks/useCurrentUser';
import { ArrayParam, useQueryParam, withDefault } from 'use-query-params';
import { IPosition } from '@medely/types';

interface IUsePositionIds {
  positionIds?: IPosition['id'][];
  positionsOptions: Partial<OptionType>[];
  clearSelectedPositionIds: () => void;
  setPositionIds: (position_ids?: IPosition['id'][]) => void;
  addPositionId: (id: string) => void;
  removePositionId: (id: string) => void;
  isPositionIdSelected: (id: string) => boolean;
  hasSelectedPositions: boolean;
}

const EMPTY_POSITION_IDS: string[] = [];

export const usePositionIds = (): IUsePositionIds => {
  const { currentUser } = useCurrentUser();

  const positionsOptions =
    currentUser?.professional?.positions?.map((p) => ({
      value: p.id.toString(),
      label: p.display_name,
    })) ?? [];

  const [position_ids, setQuery] = useQueryParam(
    'position_ids',
    withDefault(ArrayParam, EMPTY_POSITION_IDS),
  );

  const clearSelectedPositionIds = () => setQuery(undefined, 'replaceIn');

  const addPositionId = (id: string) => setQuery([...position_ids, id], 'replaceIn');

  const removePositionId = (id: string) => {
    const positionIds = position_ids.filter((positionId) => positionId !== id);

    positionIds?.length ? setQuery(positionIds, 'replaceIn') : clearSelectedPositionIds();
  };

  const isPositionIdSelected = (id: string) => position_ids.includes(id);

  const positionIds = position_ids?.length ? position_ids.map(Number) : undefined;

  const hasSelectedPositions = !!position_ids?.length;

  const setPositionIds = (position_ids?: IPosition['id'][]) =>
    setQuery(position_ids?.map(String), 'replaceIn');

  return {
    positionIds,
    positionsOptions,
    clearSelectedPositionIds,
    setPositionIds,
    addPositionId,
    removePositionId,
    isPositionIdSelected,
    hasSelectedPositions,
  };
};

export default usePositionIds;
